import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { COLORS } from "app/theme";
import { useMemo } from "react";
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  useMilkQualityFarmer,
  useMilkQualityHistory,
} from "../hooks/useInsightsDashboardQueries";
import { SCC_THRESHOLDS, TBC_THRESHOLDS } from "../milkQualityThresholds";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: 16,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
  marginBottom: 16,
});

type MetricDef = {
  label: string;
  key: string;
  suffix: string;
  description: string;
  format: (v: number) => string;
};

const METRICS: MetricDef[] = [
  {
    label: "Butterfat",
    key: "MilkQuality.fatPercent",
    suffix: "%",
    description:
      "The fat content of your milk. Higher butterfat typically commands a premium and reflects good nutrition and genetics.",
    format: (v) => v.toFixed(2),
  },
  {
    label: "Protein",
    key: "MilkQuality.proteinPercent",
    suffix: "%",
    description:
      "Milk protein content is influenced by diet, genetics, and stage of lactation. Higher protein is valued by processors.",
    format: (v) => v.toFixed(2),
  },
  {
    label: "Somatic Cell Count",
    key: "MilkQuality.somaticCellCount",
    suffix: "cells/ml",
    description:
      "SCC is an indicator of udder health. Lower counts indicate healthier cows and better milk quality. Counts below 200,000 are considered good.",
    format: (v) => Math.round(v).toLocaleString(),
  },
  {
    label: "Total Bacterial Count",
    key: "MilkQuality.totalBacterialCount",
    suffix: "cfu/ml",
    description:
      "TBC measures the cleanliness of milk production. Lower counts reflect good hygiene practices in milking and storage.",
    format: (v) => Math.round(v).toLocaleString(),
  },
];

const METRIC_COLORS = [
  COLORS.dahlia[500],
  COLORS.bellflower[500],
  COLORS.daisy[500],
  COLORS.dahlia[300],
];

const SPARKLINE_THRESHOLDS: Record<string, number[] | undefined> = {
  "MilkQuality.somaticCellCount": [SCC_THRESHOLDS.good, SCC_THRESHOLDS.warning],
  "MilkQuality.totalBacterialCount": [
    TBC_THRESHOLDS.good,
    TBC_THRESHOLDS.warning,
  ],
};

function Sparkline({
  dataKey,
  data,
  color,
}: {
  dataKey: string;
  data: { period: string; value: number }[];
  color: string;
}) {
  if (data.length < 2) return null;
  const thresholds = SPARKLINE_THRESHOLDS[dataKey];
  return (
    <Box sx={{ width: "100%", height: 60, mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
        >
          {thresholds?.map((t) => (
            <ReferenceLine
              key={t}
              y={t}
              stroke="#e5e5e5"
              strokeDasharray="3 3"
            />
          ))}
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
          />
          <Tooltip
            formatter={(v: number) => [v.toLocaleString(), ""]}
            labelFormatter={(
              _label: string,
              payload: Array<{ payload?: { period?: string } }>
            ) => payload?.[0]?.payload?.period ?? ""}
            contentStyle={{ fontSize: 12 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function MilkQualityTab({ farmerGuid }: { farmerGuid?: string }) {
  const { data: milkData, isLoading } = useMilkQualityFarmer(farmerGuid);
  const { data: historyData } = useMilkQualityHistory(farmerGuid);
  const row = milkData?.data?.[0];

  const sparklineData = useMemo(() => {
    const map = new Map<string, { period: string; value: number }[]>();
    for (const r of historyData?.data ?? []) {
      const period = String(r["MilkQuality.periodStart"]).split("T")[0];
      for (const metric of METRICS) {
        const val = Number(r[metric.key as keyof typeof r]);
        if (isNaN(val) || val === 0) continue;
        if (!map.has(metric.key)) map.set(metric.key, []);
        map.get(metric.key)?.push({ period, value: val });
      }
    }
    return map;
  }, [historyData?.data]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!row) {
    return (
      <Card>
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          No milk quality data available yet. Data will appear here once milk
          reports have been processed.
        </Typography>
      </Card>
    );
  }

  const allPeriods = (historyData?.data ?? [])
    .map((r) => String(r["MilkQuality.periodStart"]).split("T")[0])
    .filter(Boolean)
    .sort();
  const periodStart =
    allPeriods[0] ?? String(row["MilkQuality.periodStart"]).split("T")[0];
  const periodEnd =
    allPeriods[allPeriods.length - 1] ??
    String(row["MilkQuality.periodEnd"]).split("T")[0];
  const antibioticTest = row["MilkQuality.antibioticTest"];

  return (
    <Box sx={{ maxWidth: 720, mx: "auto" }}>
      <Card>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b", mb: 1 }}
        >
          Milk Quality Overview
        </Typography>
        <Typography variant="body2" sx={{ color: "#52525b", lineHeight: 1.7 }}>
          Your latest milk quality results. These metrics reflect the
          composition and hygiene of your milk production.
        </Typography>
        {periodStart && periodEnd && (
          <Typography
            variant="caption"
            sx={{ color: "#a1a1aa", display: "block", mt: 1 }}
          >
            Data period: {periodStart} – {periodEnd}
          </Typography>
        )}
        {antibioticTest && (
          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              backgroundColor:
                antibioticTest === "Pass" ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${
                antibioticTest === "Pass" ? "#bbf7d0" : "#fecaca"
              }`,
              display: "inline-block",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: antibioticTest === "Pass" ? "#166534" : "#991b1b",
              }}
            >
              Antibiotic Test: {antibioticTest}
            </Typography>
          </Box>
        )}
      </Card>

      {METRICS.map((metric, i) => {
        const val = Number(row[metric.key as keyof typeof row]);
        if (isNaN(val) || val === 0) return null;

        return (
          <Card key={metric.label}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: METRIC_COLORS[i % METRIC_COLORS.length],
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#18181b" }}
              >
                {metric.label}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "1.75rem",
                fontWeight: 700,
                color: "#18181b",
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              {metric.format(val)}
              <Typography
                component="span"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#71717a",
                  ml: 0.5,
                }}
              >
                {metric.suffix}
              </Typography>
            </Typography>

            <Sparkline
              dataKey={metric.key}
              data={sparklineData.get(metric.key) ?? []}
              color={METRIC_COLORS[i % METRIC_COLORS.length]}
            />

            <Typography
              variant="body2"
              sx={{
                color: "#52525b",
                lineHeight: 1.6,
                fontSize: "0.825rem",
                mt: 1,
              }}
            >
              {metric.description}
            </Typography>
          </Card>
        );
      })}
    </Box>
  );
}
